"use client";

import { useCallback, type MutableRefObject, type RefObject } from "react";
import { selectTimelinePeriodScrollLeft } from "./timeline-scroll-selectors";

export interface UseTimelinePeriodScrollerInput {
  centeredPeriodIndex: number;
  periodCount: number;
  periodRefs: MutableRefObject<Array<HTMLDivElement | null>>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

export interface TimelinePeriodScroller {
  handleScrollNext: () => void;
  handleScrollPrev: () => void;
}

export function useTimelinePeriodScroller({
  centeredPeriodIndex,
  periodCount,
  periodRefs,
  scrollContainerRef,
}: UseTimelinePeriodScrollerInput): TimelinePeriodScroller {
  const scrollToPeriod = useCallback(
    (periodIndex: number): void => {
      const scrollContainerNode = scrollContainerRef.current;
      const periodNode = periodRefs.current[periodIndex];
      if (!scrollContainerNode || !periodNode) {
        return;
      }

      scrollContainerNode.scrollTo({
        left: selectTimelinePeriodScrollLeft({
          periodNode,
          scrollContainerNode,
        }),
        behavior: "smooth",
      });
    },
    [periodRefs, scrollContainerRef],
  );

  const handleScrollPrev = useCallback((): void => {
    const targetIndex = Math.max(0, centeredPeriodIndex - 1);
    if (targetIndex === centeredPeriodIndex) {
      return;
    }

    scrollToPeriod(targetIndex);
  }, [centeredPeriodIndex, scrollToPeriod]);

  const handleScrollNext = useCallback((): void => {
    if (periodCount <= 0) {
      return;
    }

    const maxIndex = periodCount - 1;
    const targetIndex = Math.min(maxIndex, centeredPeriodIndex + 1);
    if (targetIndex === centeredPeriodIndex) {
      return;
    }

    scrollToPeriod(targetIndex);
  }, [centeredPeriodIndex, periodCount, scrollToPeriod]);

  return {
    handleScrollNext,
    handleScrollPrev,
  };
}
